import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, Grid, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert, TablePagination
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import DraftsIcon from '@mui/icons-material/Drafts'
import InventoryIcon from '@mui/icons-material/Inventory'
import { dcApi } from '../services/dc.api'
import { DCPurchase } from '../types/dc.types'

const statusColors: Record<string, 'default' | 'primary' | 'success'> = {
  draft: 'default',
  confirmed: 'primary',
  inventoried: 'success'
}

export default function DCPurchasesPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  const { data, isLoading, error } = useQuery({
    queryKey: ['purchases', page, rowsPerPage],
    queryFn: () => dcApi.getPurchases({ page: page + 1, limit: rowsPerPage })
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Failed to load purchases: {(error as Error).message}</Alert>
  }

  const purchases = data?.purchases || []
  const total = data?.pagination?.total || 0

  const draftCount = purchases.filter((p: DCPurchase) => p.status === 'draft').length
  const inventoriedCount = purchases.filter((p: DCPurchase) => p.status === 'inventoried').length

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Purchase Bills</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditNoteIcon />}
            onClick={() => navigate('/purchases/manual')}
          >
            Manual Entry
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/purchases/upload')}
          >
            Upload CSV
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ReceiptLongIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h6">{total} Total Purchases</Typography>
                <Typography variant="body2" color="text.secondary">
                  All purchase bills
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DraftsIcon color="action" fontSize="large" />
              <Box>
                <Typography variant="h6">{draftCount} Draft</Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending confirmation
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <InventoryIcon color="success" fontSize="large" />
              <Box>
                <Typography variant="h6">{inventoriedCount} Inventoried</Typography>
                <Typography variant="body2" color="text.secondary">
                  Added to inventory
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Purchases Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Purchase #</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Distributor</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Matched</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No purchase bills yet. Upload a bill to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              purchases.map((purchase: DCPurchase) => (
                <TableRow
                  key={purchase.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/purchases/${purchase.id}`)}
                >
                  <TableCell>
                    <Typography fontWeight={600}>{purchase.purchase_number}</Typography>
                  </TableCell>
                  <TableCell>{purchase.invoice_number || '-'}</TableCell>
                  <TableCell>{purchase.invoice_date || '-'}</TableCell>
                  <TableCell>{purchase.distributor_name || purchase.distributor_db_name || '-'}</TableCell>
                  <TableCell align="right">{purchase.total_items}</TableCell>
                  <TableCell align="right">{purchase.matched_items}</TableCell>
                  <TableCell align="right">
                    Rs. {(purchase.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={purchase.status}
                      size="small"
                      color={statusColors[purchase.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {purchase.created_at
                        ? new Date(purchase.created_at).toLocaleDateString('en-IN')
                        : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>
    </Box>
  )
}
